import { Alert, IconButton, Snackbar, Tooltip } from '@mui/material';
import { useCreateFileUrl } from '_api/index';
import { PresignedUrl } from '_api/types';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { MdAttachFile } from 'react-icons/md';
import { uploadFile, md5Base64, generateFileChallenge } from 'utils/media';

import {
  FileAttachmentButtonProps,
  FileExtention,
  FILE_EXTENSION_TO_MIME_MAP,
} from '../types';

export const FileAttachmentButton = ({
  disabled,
  onFileSelect,
  isUploadingFile,
  setFileName,
}: FileAttachmentButtonProps) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [extension, setExtention] = useState<FileExtention | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => isUploadingFile(uploadingFile), [uploadingFile]);

  const { mutateAsync: createUploadUrl } = useCreateFileUrl();

  const getFileType = (ext: string) => {
    const fileType = ext.split('.').pop();
    const mimeType = FILE_EXTENSION_TO_MIME_MAP[fileType || ''];
    if (mimeType) {
      setExtention(mimeType);
    } else {
      onError({ message: 'Unsupported file type' }, handleRemove);
    }
  };

  const onSuccess = (data: PresignedUrl | null) => {
    if (data) {
      onFileSelect(data.key);
    }
    setUploadingFile(false);
  };

  const onError = (err: any, onClear?: () => void) => {
    setShowError(true);
    setErrorMessage(err.message);
    setTimeout(() => {
      setShowError(false);
      if (onClear) {
        onClear();
      }
    }, 3500);
  };

  useEffect(() => {
    const upload = async () => {
      if (!file || !extension) {
        return null;
      }

      setUploadingFile(true);
      const checksum = await md5Base64(file);
      const challenge = await generateFileChallenge(file);
      const uploadUrl = await createUploadUrl({
        filename: file.name,
        contentType: extension,
        checksum,
        challenge,
      });

      await uploadFile(uploadUrl.url, file, extension);

      return uploadUrl;
    };

    if (file && extension !== undefined) {
      setUploadingFile(true);
      upload().then(onSuccess).catch(onError);
    }
  }, [file, extension]);

  const handleButtonClick = () => {
    // @ts-ignore
    fileInputRef.current.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputFile = event.target.files ? event.target.files[0] : null;
    if (inputFile) {
      getFileType(inputFile.name);
      setFile(inputFile);
      setFileName(inputFile.name);
    }
  };

  const handleRemove = () => {
    setFile(null);
    setExtention(undefined);
    setFileName('');
  };

  return (
    <>
      {!file ? (
        <IconButton disabled={disabled} onClick={handleButtonClick}>
          <MdAttachFile />
        </IconButton>
      ) : (
        <Tooltip title={'Remove Attachment: Only one permitted'} placement="top">
          <IconButton disabled={disabled} onClick={handleRemove}>
            <IoMdClose />
          </IconButton>
        </Tooltip>
      )}
      <input
        type="file"
        accept="application/pdf, image/*, video/mp4, video/webm, video/ogg"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        open={showError}
      >
        <Alert onClose={() => setShowError(false)} severity="error" variant="filled">
          Failed to upload file
          <br />
          {errorMessage}
          <br />
          Resolve issues and try again.
        </Alert>
      </Snackbar>
    </>
  );
};
